import { Modal } from 'core/components/ui/Modal'
import axiosInstance from 'core/network/axiosInstance'
import React, { useEffect, useState } from 'react'
import { OrderListItem } from '../../domain/entities/Order'

interface ClubIndentsModalProps {
  isOpen: boolean
  onClose: () => void
  selectedOrders: OrderListItem[]
  productMoq: number
  onOrdersRaised: () => void
}

const getUnits = (order: OrderListItem) =>
  order.totalUnits || order.items?.reduce((sum, item) => sum + item.units, 0) || 0

export const ClubIndentsModal: React.FC<ClubIndentsModalProps> = ({
  isOpen,
  onClose,
  selectedOrders,
  productMoq,
  onOrdersRaised,
}) => {
  const [poNumber, setPoNumber] = useState('')

  const [raising, setRaising] = useState(false)
  const [errorMsg, setErrorMsg] = useState('')
  const [successPoNumber, setSuccessPoNumber] = useState<string | null>(null)

  useEffect(() => {
    if (!isOpen) return

    setErrorMsg('')
    setSuccessPoNumber(null)
    setPoNumber(`PO-${Date.now().toString().slice(-6)}`)
  }, [isOpen])

  const totalUnits = selectedOrders.reduce((sum, o) => sum + getUnits(o), 0)
  const moqReached = totalUnits >= productMoq
  const progressPct = Math.min(100, Math.round((totalUnits / Math.max(1, productMoq)) * 100))
  const firstItem = selectedOrders[0]?.items?.[0]

  const handleRaiseOrder = async () => {
    if (!moqReached || selectedOrders.length === 0 || !poNumber.trim()) return

    setRaising(true)
    setErrorMsg('')
    try {
      const orderIds = selectedOrders.map((o) => o.id)
      const response = await axiosInstance.post(
        '/api/v1/purchase-orders',
        {
          indent_ids: orderIds,
          po_number: poNumber.trim(),
        },
        {
          responseType: 'blob',
        },
      )

      const blob = new Blob([response.data], { type: 'application/pdf' })
      const url = window.URL.createObjectURL(blob)
      const a = document.createElement('a')
      a.href = url
      a.download = `PO-${poNumber.trim()}.pdf`
      document.body.appendChild(a)
      a.click()
      document.body.removeChild(a)
      window.URL.revokeObjectURL(url)

      setSuccessPoNumber(poNumber.trim())
      onOrdersRaised()
    } catch (err: unknown) {
      const errorVal = err as Error
      setErrorMsg(errorVal.message || 'Failed to release the clubbed purchase order.')
    } finally {
      setRaising(false)
    }
  }

  const handleClose = () => {
    onClose()
  }

  const footerActions = successPoNumber ? (
    <button
      onClick={handleClose}
      className="px-4 py-2 text-sm font-semibold border border-border rounded-btn bg-surface hover:bg-bg text-text-secondary hover:text-text-primary transition-colors"
    >
      Close
    </button>
  ) : (
    <>
      <button
        type="button"
        onClick={handleClose}
        disabled={raising}
        className="px-4 py-2 text-sm font-semibold border border-border rounded-btn bg-surface hover:bg-bg text-text-secondary hover:text-text-primary transition-colors disabled:opacity-50"
      >
        Cancel
      </button>
      <button
        type="button"
        onClick={handleRaiseOrder}
        disabled={!moqReached || raising || !poNumber.trim()}
        className="px-4 py-2 text-sm font-semibold bg-primary hover:bg-primary-hover text-primary-foreground rounded-btn transition-colors disabled:opacity-50 min-w-[8rem] flex items-center justify-center"
      >
        {raising ? (
          <svg className="animate-spin h-4 w-4" fill="none" viewBox="0 0 24 24">
            <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
            <path
              className="opacity-75"
              fill="currentColor"
              d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
            />
          </svg>
        ) : (
          'Release PO'
        )}
      </button>
    </>
  )

  return (
    <Modal
      isOpen={isOpen}
      onClose={handleClose}
      title="Club Indents & Release Purchase Order"
      footer={footerActions}
      size="lg"
    >
      {errorMsg && (
        <div className="p-3 mb-4 text-xs bg-danger/10 border border-danger/20 text-danger rounded-btn font-medium">
          {errorMsg}
        </div>
      )}

      {successPoNumber ? (
        <div className="flex flex-col items-center text-center gap-3 py-6">
          <div className="h-12 w-12 rounded-full bg-status-active/10 text-status-active flex items-center justify-center">
            <svg className="h-6 w-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2.5" d="M5 13l4 4L19 7" />
            </svg>
          </div>
          <p className="font-bold text-text-primary text-sm">
            Purchase Order <span className="font-mono text-primary">{successPoNumber}</span> Released Successfully!
          </p>
          <p className="text-xs text-text-secondary">
            Merged {selectedOrders.length} indent{selectedOrders.length > 1 ? 's' : ''} ({totalUnits} Units) for{' '}
            <span className="font-semibold text-text-primary">{firstItem?.productName}</span>.
          </p>
        </div>
      ) : (
        <div className="space-y-4 text-left">
          {/* MOQ Progress Banner */}
          <div className="border border-border rounded-card p-3.5 bg-bg/40 space-y-2">
            <div className="flex items-center justify-between text-xs font-semibold">
              <span className="text-text-secondary">
                Product:{' '}
                <span className="font-bold text-text-primary">
                  {firstItem?.productName || 'Catalog Product'} ({firstItem?.variant || firstItem?.unit || 'Bag'})
                </span>
              </span>
              <span className={moqReached ? 'text-status-active font-bold' : 'text-text-primary'}>
                {totalUnits} / {productMoq} Units
              </span>
            </div>
            <div className="h-2 rounded-full bg-border overflow-hidden">
              <div
                className={`h-full rounded-full transition-all duration-300 ${moqReached ? 'bg-status-active' : 'bg-primary'}`}
                style={{ width: `${progressPct}%` }}
              />
            </div>
          </div>

          {/* Form Fields: Manufacturer & PO Number */}
          <div className="grid grid-cols-1 gap-4">
            <div className="space-y-1.5">
              <label className="text-xs font-bold text-text-primary uppercase tracking-wider block">
                PO Reference Number <span className="text-danger">*</span>
              </label>
              <input
                type="text"
                value={poNumber}
                onChange={(e) => setPoNumber(e.target.value)}
                placeholder="PO-2026-001"
                className="w-full px-3 py-2 border border-border rounded-btn bg-bg text-text-primary text-xs focus:outline-none focus:ring-2 focus:ring-primary/20 font-mono"
              />
            </div>
          </div>

          {/* Selected Indents Breakdown */}
          <div className="space-y-2">
            <h5 className="text-xs font-bold text-text-primary uppercase tracking-wider">
              Selected Indents ({selectedOrders.length})
            </h5>
            <div className="border border-border rounded-card divide-y divide-border max-h-48 overflow-y-auto bg-surface">
              {selectedOrders.map((order) => {
                const units = getUnits(order)
                return (
                  <div key={order.id} className="flex items-center justify-between p-2.5 text-xs">
                    <div>
                      <p className="font-semibold text-text-primary">
                        {order.buyer?.businessName || order.buyer?.name || 'Buyer'}
                      </p>
                      <p className="text-[10px] text-text-secondary font-mono">{order.id}</p>
                    </div>
                    <span className="font-bold text-text-primary">{units} Units</span>
                  </div>
                )
              })}
            </div>
          </div>
        </div>
      )}
    </Modal>
  )
}
